import React, { useState, useEffect } from "react";
import { Chrono } from "react-chrono";
import { Button, DropdownButton, Dropdown } from "react-bootstrap";
import SingleAppointment from "./SingleAppointment";
import { BsReplyFill, BsFillCalendarPlusFill, BsInfoCircleFill } from "react-icons/bs";
import ApiService from "../../../../ApiService";
import { scheduleFollowUp } from "../../../../util/common";
import { clinicianIdFromRefreshAuth } from "../../../../util/authUtil";
import { REMINDER_CALENDAR_LINK } from "../../../../util/bookingUtils";
import { MonitorService } from "../../../../util/captureException";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
const APPOINTMENT = {
  UPCOMING: "upcoming",
  PAST: "past"
};

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

function filterAppointments(appointments) {
  const currentTime = new Date();
  const upcomingAppointments = [];
  const pastAppointments = [];

  appointments.forEach(appnt => {
    if (new Date(appnt?.appntEnd) > currentTime) {
      upcomingAppointments.push(appnt);
    } else {
      pastAppointments.push(appnt);
    }
  });

  return {
    upcoming: upcomingAppointments,
    past: pastAppointments
  };
}

function formatDate(inputDate) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const date = new Date(inputDate);

  if (isNaN(date.getTime())) {
    return "NA"; // Return "NA" if the date is invalid
  }
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date
    .getFullYear()
    .toString()
    .substr(-2);

  return `${day} ${month} '${year}`;
}
function formatTimeRange(appntStart, appntEnd) {
  if (!appntStart || !appntEnd) {
    return "NA"; // Return "NA" if the date is invalid
  }

  const parseTime = dateTimeString => {
    const dateObject = new Date(dateTimeString);
    const hours = dateObject.getHours();
    const minutes = dateObject
      .getMinutes()
      .toString()
      .padStart(2, "0");
    const period = hours >= 12 ? "pm" : "am";
    const formattedHours = (hours % 12 || 12).toString().padStart(2, "0");
    return `${formattedHours}:${minutes} ${period}`;
  };

  const formattedStartTime = parseTime(appntStart);
  const formattedEndTime = parseTime(appntEnd);

  return `${formattedStartTime} - ${formattedEndTime}`;
}

function generateNames(items) {
  const names = items?.map((item, index) => `${item.name}`) || [];
  return names?.join(" / ") || "NA";
}

export default function Appointment({ patientId, patientEmail, patientDetail }) {
  const [activeTab, setActiveTab] = useState(APPOINTMENT.UPCOMING);
  const [appointments, setAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [pastAppointments, setPastAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [clinician, setClinician] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackText, setSnackText] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("");
  const fetchAppointment = async () => {
    try {
      setIsLoading(true);
      if (!patientEmail) {
        handleSnackOpen("Patient email is required to fetch appointments", "error");
        setIsLoading(false);
        return;
      }
      const res = await ApiService.getAllPatientBookings(patientEmail);
      setAppointments(res?.data);
      setIsLoading(false);
    } catch (error) {
      MonitorService.captureException(error);
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchAppointment();
  }, [patientEmail]);

  useEffect(() => {
    setUpcomingAppointments(filterAppointments(appointments)?.upcoming);
    setPastAppointments(filterAppointments(appointments)?.past);
  }, [appointments]);

  const handleSnackOpen = (text, severity) => {
    setSnackSeverity(severity);
    setSnackText(text);
    setSnackOpen(true);
  };

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const clinicianId = await clinicianIdFromRefreshAuth();
        if (clinicianId) {
          const res = await ApiService.getClinicianProfile(clinicianId);
          setClinician(res?.data);
        }
      } catch (error) {
        MonitorService.captureException(error);
        handleSnackOpen("Error fetching clinician profile", "error");
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const onlineReminder = async patientDetail => {
    try {
      if (!patientDetail || !patientDetail?.firstName) {
        handleSnackOpen("Patient details are missing", "error");
        return;
      }
      if (!clinician || !clinician?.tbdEmail || !clinician?.firstName || !clinician?.lastName) {
        handleSnackOpen("Clinician details are missing", "error");
        return;
      }
      const notes = `Patient Name: ${patientDetail?.firstName}\nPatient ID: ${patientId}`;
      const payload = {
        name: `Clinician ${clinician?.firstName} ${clinician?.lastName}`,
        email: clinician?.tbdEmail,
        phone: clinician?.mobile || "Unknown",
        notes: notes,
        patientId: patientId
      };
      const queryParams = new URLSearchParams(payload).toString();
      const calWithQueryParams = `${REMINDER_CALENDAR_LINK}?${queryParams}`;
      window.open(calWithQueryParams, "_blank");
    } catch (error) {
      MonitorService.captureException(error);
      handleSnackOpen("Error scheduling online reminder appointment", "error");
    }
  };

  return (
    <div className="dashboardContent">
      {/* //& Snack Bar */}
      <Snackbar open={snackOpen} autoHideDuration={6000} onClose={handleSnackClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={handleSnackClose} severity={snackSeverity}>
          {snackText}
        </Alert>
      </Snackbar>

      <div className="tabsBackground">
        <button
          onClick={() => setActiveTab(APPOINTMENT.UPCOMING)}
          className={`contentButton ${activeTab === APPOINTMENT.UPCOMING && "contentButtonActive"}`}
        >
          Upcoming Appointment
        </button>
        <button onClick={() => setActiveTab(APPOINTMENT.PAST)} className={`contentButton ${activeTab === APPOINTMENT.PAST && "contentButtonActive"}`}>
          Past Appointments
        </button>
      </div>

      <DropdownButton
        id="dropdown-basic-button"
        title="Follow up Appointment"
        variant="primary"
        className="ml-3"
        size="md"
        style={{
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          justifySelf: "flex-end"
        }}
      >
        <Dropdown.Item onClick={() => scheduleFollowUp(patientDetail, "NV")}>
          Follow up Appointment for Vegas&nbsp;&nbsp;
          <BsFillCalendarPlusFill />
        </Dropdown.Item>
        <Dropdown.Item onClick={() => scheduleFollowUp(patientDetail, "CO")}>
          Follow up Appointment for Denver&nbsp;&nbsp;
          <BsFillCalendarPlusFill />
        </Dropdown.Item>
        <Dropdown.Item onClick={() => onlineReminder(patientDetail)}>
          Reminder Appointment&nbsp;&nbsp;
          <BsFillCalendarPlusFill />
        </Dropdown.Item>
      </DropdownButton>

      <div className="tabsContent">
        {activeTab === APPOINTMENT.UPCOMING && upcomingAppointments?.length > 0 ? (
          <Chrono hideControls disableNavOnKey theme={{ primary: "#a8a8a8", secondary: "green" }} mode="VERTICAL">
            {upcomingAppointments?.map((appnt, index) => (
              <SingleAppointment
                key={index}
                date={formatDate(appnt?.appntStart)}
                time={formatTimeRange(appnt?.appntStart, appnt?.appntEnd)}
                product={generateNames(appnt?.products)}
                clinicanName={appnt?.primaryClinicianId ? appnt?.primaryClinicianName : "NA"}
                bookingId={appnt?.id}
              />
            ))}
          </Chrono>
        ) : activeTab === APPOINTMENT.PAST ? (
          pastAppointments?.length > 0 ? (
            <Chrono hideControls disableNavOnKey theme={{ primary: "#a8a8a8", secondary: "green" }} mode="VERTICAL">
              {pastAppointments?.map((appnt, index) => (
                <SingleAppointment
                  key={index}
                  date={formatDate(appnt?.appntStart)}
                  time={formatTimeRange(appnt?.appntStart, appnt?.appntEnd)}
                  product={generateNames(appnt?.products)}
                  clinicanName={appnt?.primaryClinicianId ? appnt?.primaryClinicianName : "NA"}
                  bookingId={appnt?.id}
                />
              ))}
            </Chrono>
          ) : (
            <div className="tabsContent">
              {isLoading ? <div className="loading"></div> : <h1 className="noRecordFound">No Past Appointments found</h1>}
            </div>
          )
        ) : (
          <div className="tabsContent">
            {isLoading ? <div className="loading"></div> : <h1 className="noRecordFound">No Upcoming Appointments found</h1>}
          </div>
        )}
      </div>
    </div>
  );
}
